import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import StatBox from '../components/StatBox';
import Footer from '../components/Footer';

// Hero
import customerhero from '../assets/customerhero.png';

// Decision 1: menu hierarchy
import customerHome from '../assets/zipem-home.png';
import restaurantMenu from '../assets/zipem-restaurant-menu.png';

// Decision 2: repeat selection
import foodCustomization from '../assets/zipem-customize.png';
import repeatCustomization from '../assets/zipem-customize-repeat.png';

// Decision 3: Daily Meals subscriptions
import subscriptionHome from '../assets/zipem-subscription-home.png';
import subscriptionPrograms from '../assets/zipem-subscription-programs.png';
import subscriptionList from '../assets/zipem-subscription-list.png';
import subscriptionItems from '../assets/zipem-subscription-items.png';
import subscriptionSchedule from '../assets/zipem-subscription-schedule.png';

// After the tap: restaurant + delivery
import restaurantDashboard from '../assets/zipem-restaurant-dashboard.png';
import restaurantOrderDetails from '../assets/zipem-restaurant-order-details.png';
import deliveryOrderDetails from '../assets/zipem-delivery-order-details.png';
import deliveryOrderMap from '../assets/zipem-delivery-order-map.png';

// More screens gallery
import offersScreen from '../assets/zipem-offers.png';
import vouchersScreen from '../assets/zipem-vouchers.png';
import cartScreen from '../assets/zipem-cart.png';
import restaurantFoodEditor from '../assets/zipem-restaurant-food-editor.png';
import restaurantSalesReport from '../assets/zipem-restaurant-sales-report.png';
import deliveryHome from '../assets/zipem-delivery-home.png';

// Not used on purpose: zipem-restaurant-reviews.png (shows lorem ipsum),
// zipem-restaurant-new-orders.png, zipem-restaurant-offers.png,
// zipem-restaurant-menu-availability.png, zipem-restaurant-dashboard-orders.png,
// zipem-delivery-orders.png. Re-add only if you replace them with clean screens.

const stats = [
  { number: '₹20L', label: 'Peak Monthly Revenue' },
  { number: '280', label: 'Orders Per Day' },
  { number: '110', label: 'Partner Restaurants' },
  { number: '5K', label: 'Total Users' },
];

// Fill in `result` on each decision once you have a real number or observation
// (A/B outcome, items per order, reorder rate, subscriber count, even a
// qualitative owner reaction). A decision with result: null simply omits that line.
const decisions = {
  menu: {
    problem:
      'At first, we showed a photo beside every dish. On a menu with around 40 items, that made the page long and busy. A popular dish and one that rarely sold got the same amount of space.',
    hypothesis:
      'We thought photos were most useful for dishes customers ordered often or needed help recognising. For the rest, the dish name and price could be enough.',
    change:
      'I looked at sales for individual dishes and discussed the menu with restaurant owners. Together, we picked the dishes that should keep their photos. The others became simple text rows, so customers could still see the full menu without every item taking up the same space.',
    result: null, // TODO: e.g. change in menu scroll depth, items per order, or owner feedback
    learning:
      'I learned that a menu does not need to give every dish equal space. Photos help where they make a choice easier; elsewhere, a clear name and price keep the menu moving.',
  },
  repeat: {
    problem:
      'When customers reordered a dish, they often had to choose the same portion, add-ons or special requests all over again.',
    hypothesis:
      'Showing the previous choices could save time for returning customers, as long as they could still change them or start fresh.',
    change:
      'I kept the options inside the item customisation screen rather than adding more controls to the menu. When a customer came back to a dish, they could reuse their last choices or make new ones.',
    result: null, // TODO: e.g. share of reorders using "Reuse", or time to reorder
    learning:
      "Remembering a customer's last choice is useful, but they should always be able to change it.",
  },
  subscription: {
    problem:
      'We saw an opportunity to serve people who wanted meals regularly, without having to place a new order every day.',
    hypothesis:
      'We wanted customers to start with the meal they needed—breakfast, lunch, dinner or beverages—and understand the package before subscribing.',
    change:
      'I gave Daily Meals its own entry on the home screen. From there, customers could choose a meal category, compare packages, change a meal for a particular day and select a start date using the weekly or monthly calendar.',
    result: null, // TODO: e.g. number of subscribers, share of orders, retention
    learning:
      'For a subscription, customers need to see what they will receive and when. The weekly plan and start date were as important as the final payment.',
  },
};

const ImageFigure = ({ src, alt, caption, className = 'image-full', onOpen }) => (
  <figure className={className}>
    <button
      type="button"
      className="zipem-image-button"
      onClick={() => onOpen(src, alt)}
      aria-label={`Enlarge ${alt}`}
    >
      <img src={src} alt={alt} loading="lazy" />
    </button>
    {caption && <figcaption>{caption}</figcaption>}
  </figure>
);

const Decision = ({ number, title, data, children }) => (
  <div className="challenge-block">
    <h3>
      {number}. {title}
    </h3>
    <p>
      <strong>Problem:</strong> {data.problem}
    </p>
    <p>
      <strong>What I thought might help:</strong> {data.hypothesis}
    </p>
    <p>
      <strong>What I changed:</strong> {data.change}
    </p>
    {data.result && (
      <p>
        <strong>Result:</strong> {data.result}
      </p>
    )}
    <p>
      <strong>What I learned:</strong> {data.learning}
    </p>
    {children}
  </div>
);

const FoodDeliveryCase = () => {
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!lightboxImage) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setLightboxImage(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightboxImage]);

  const openLightbox = (image, alt) => setLightboxImage({ image, alt });
  const closeLightbox = () => setLightboxImage(null);

  return (
    <PageTransition>
      <nav
        className="zipem-nav"
        style={{
          position: 'fixed',
          top: 0,
          width: '100%',
          background: 'var(--white)',
          borderBottom: '3px solid var(--black)',
          zIndex: 1000,
          padding: '1.5rem 5%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Link
          to="/"
          style={{
            fontWeight: 700,
            color: 'var(--black)',
            textDecoration: 'none',
            fontSize: '1.1rem',
          }}
        >
          ← Back to Home
        </Link>
        <div style={{ fontWeight: 900, fontSize: '1.2rem' }}>Case Study</div>
      </nav>

      {/* HERO */}
      <section
        style={{
          marginTop: '5rem',
          background: 'var(--black)',
          color: 'var(--white)',
          padding: '6rem 8rem',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div className="hero-label">Case Study 02</div>
        <h1
          style={{
            fontSize: 'clamp(3rem, 6vw, 5rem)',
            fontWeight: 900,
            lineHeight: 1,
            marginBottom: '2rem',
            letterSpacing: '-2px',
            color: 'var(--white)',
          }}
        >
          Zipem Food Delivery
        </h1>
        <div className="hero-result">
          From 15 to 110 restaurants: building a food-ordering experience for a tier-2 city
        </div>

        <div className="hero-meta">
          <div>
            <div className="meta-label">Role</div>
            <div className="meta-value">Co-founder · Product &amp; Design Lead</div>
          </div>
          <div>
            <div className="meta-label">Duration</div>
            <div className="meta-value">2015–2020</div>
          </div>
          <div>
            <div className="meta-label">Team</div>
            <div className="meta-value">1 Designer, 4 Developers</div>
          </div>
          <div>
            <div className="meta-label">Platform</div>
            <div className="meta-value">Customer app + connected tools</div>
          </div>
        </div>
      </section>

      {/* HERO IMAGE */}
      <section className="hero-image">
        <div
          className="image-placeholder"
          style={{
            width: '100%',
            height: 'auto',
            padding: 0,
            border: 'none',
            background: 'transparent',
            overflow: 'hidden',
          }}
        >
          <img
            src={customerhero}
            alt="Zipem food delivery platform"
            style={{
              display: 'block',
              width: '100%',
              maxWidth: '100%',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </div>
      </section>

      <div className="content">
        {/* TL;DR */}
        <section className="section">
          <h2 className="section-title">The Short Version</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              border: '3px solid var(--black)',
            }}
          >
            {[
              [
                'The problem',
                'Customers had to work through long menus and repeat the same customisation choices. Behind every order, the restaurant, packing team and delivery partner also needed the right information.',
              ],
              [
                'My role',
                'I co-founded Zipem and led product and design. I worked with four developers, helped build the front end and personally onboarded restaurant partners.',
              ],
              [
                'The outcome',
                'We launched with 15 restaurants, grew to 110 partners and reached 280 orders a day, with peak monthly revenue of ₹20L. We closed in 2020 after the pandemic disrupted operations.',
              ],
            ].map(([label, text]) => (
              <div
                key={label}
                style={{ padding: '1.5rem', borderRight: '1px solid var(--black)' }}
              >
                <p style={{ fontWeight: 900, marginBottom: '0.5rem' }}>{label}</p>
                <p className="section-text" style={{ margin: 0 }}>
                  {text}
                </p>
              </div>
            ))}
          </div>
        </section>

        <div className="stats-showcase">
          {stats.map((stat) => (
            <StatBox key={stat.label} number={stat.number} label={stat.label} />
          ))}
        </div>

        {/* ROLE */}
        <section className="section">
          <h2 className="section-title">What I Owned</h2>
          <p className="section-text">
            <strong>My role:</strong> I shaped the product, decided what to
            prioritise and designed the experiences across the apps. I also
            onboarded restaurants and handled customer support. That meant I
            heard directly when something was confusing or getting in the way.
          </p>
          <p className="section-text">
            <strong>Working with the team:</strong> four developers built the
            platform using Onsen UI, HTML/CSS/JS, PHP Laravel, MySQL and AWS.
            I worked with them during implementation and contributed to the
            front end.
          </p>
          <div className="tech-stack">
            <div className="tech-item">Onsen UI</div>
            <div className="tech-item">HTML/CSS/JS</div>
            <div className="tech-item">PHP Laravel</div>
            <div className="tech-item">MySQL</div>
            <div className="tech-item">AWS</div>
          </div>
        </section>

        {/* TIMELINE */}
        <section className="section">
          <h2 className="section-title">The Journey</h2>
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-date">2015</div>
              <div className="timeline-content">
                <h3>Research &amp; Planning</h3>
                <p>
                  Looked at the opportunity for food delivery in a tier-2 city and worked out what the first version of Zipem needed to do.
                </p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-date">Late 2016</div>
              <div className="timeline-content">
                <h3>Launch</h3>
                <p>
                  We built the customer app and the tools needed by restaurants, packers and delivery partners. Zipem launched in one city with 15 restaurants.
                </p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-date">2017</div>
              <div className="timeline-content">
                <h3>Growth</h3>
                <p>110 restaurant partners and a peak of 280 orders a day.</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-date">2017–2019</div>
              <div className="timeline-content">
                <h3>Product Evolution</h3>
                <p>
                  We kept improving the product. Customer conversations and sales data helped shape changes to menus, food customisation, repeat orders and Daily Meals subscriptions.
                </p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-date">Mar–Mid 2020</div>
              <div className="timeline-content">
                <h3>COVID-19 and Closure</h3>
                <p>
                  The lockdown stopped restaurant and delivery operations.
                  Without external funding, we shut Zipem down.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* HOW I WORKED */}
        <section className="section">
          <h2 className="section-title">How I Made Decisions</h2>
          <p className="section-text">
            I was in regular contact with customers, restaurant owners and delivery partners. I also looked at sales data and used A/B tests when comparing ideas. Here are a few examples of changes that came out of those conversations and observations.
          </p>
        </section>

        {/* DECISIONS */}
        <section className="section">
          <h2 className="section-title">A Few Decisions That Shaped Zipem</h2>

          <Decision
            number="1"
            title="Using photos where they help"
            data={decisions.menu}
          >
            <div className="image-grid zipem-image-grid">
              <ImageFigure
                className="image-grid-item"
                src={customerHome}
                alt="Zipem customer app home screen"
                caption="Home screen: search, cuisines, restaurants and offers"
                onOpen={openLightbox}
              />
              <ImageFigure
                className="image-grid-item"
                src={restaurantMenu}
                alt="Zipem restaurant menu with images for popular dishes and text rows for the rest"
                caption="Menu: photos for popular dishes, simple text rows for the rest"
                onOpen={openLightbox}
              />
            </div>
          </Decision>

          <Decision
            number="2"
            title="Making repeat orders easier"
            data={decisions.repeat}
          >
            <div className="image-grid zipem-image-grid">
              <ImageFigure
                className="image-grid-item"
                src={foodCustomization}
                alt="Zipem food customisation options"
                caption="Customising portion, requests and add-ons"
                onOpen={openLightbox}
              />
              <ImageFigure
                className="image-grid-item"
                src={repeatCustomization}
                alt="Zipem prompt to reuse a previous selection or choose new"
                caption="On a repeat order: use the last choices or make new ones"
                onOpen={openLightbox}
              />
            </div>
          </Decision>

          <Decision
            number="3"
            title="Making regular meals easier to plan"
            data={decisions.subscription}
          >
            <div className="image-grid zipem-image-grid">
              <ImageFigure
                className="image-grid-item"
                src={subscriptionHome}
                alt="Zipem home screen with a Daily Meals entry"
                caption="Daily Meals on the home screen"
                onOpen={openLightbox}
              />
              <ImageFigure
                className="image-grid-item"
                src={subscriptionPrograms}
                alt="Zipem subscription meal categories"
                caption="Start from Breakfast, Lunch, Dinner or Beverages"
                onOpen={openLightbox}
              />
              <ImageFigure
                className="image-grid-item"
                src={subscriptionList}
                alt="Zipem subscription package list with subscribe and customise actions"
                caption="Compare packages, then subscribe or customise"
                onOpen={openLightbox}
              />
              <ImageFigure
                className="image-grid-item"
                src={subscriptionItems}
                alt="Zipem package detail with the daily meal and addable items"
                caption="Review the day's meal and add items"
                onOpen={openLightbox}
              />
              <ImageFigure
                className="image-grid-item"
                src={subscriptionSchedule}
                alt="Zipem subscription schedule with per-day changes and calendar start date"
                caption="Change a meal for a day and choose when the plan starts"
                onOpen={openLightbox}
              />
            </div>
          </Decision>

          <p className="section-text">
            Offers showed the discount alongside the minimum order value, so customers could check the condition before using them. In the cart, I kept the items, offers, tip and bill together for a final review.
          </p>
        </section>

        {/* AFTER THE TAP */}
        <section className="section">
          <h2 className="section-title">What Happens After an Order Is Placed</h2>
          <p className="section-text">
            Once a customer placed an order, the work continued across the restaurant, packing station and delivery partner. I had to think about what each person needed to see and do, not just what happened in the customer app.
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: '1rem',
              margin: '2rem 0',
            }}
          >
            {[
              ['Customer app', 'Discover, customise, pay'],
              ['Restaurant app', 'Accept and prepare'],
              ['Packing app', 'Pack and hand off'],
              ['Delivery app', 'Pick up and deliver'],
            ].map(([name, job]) => (
              <div
                key={name}
                style={{ border: '3px solid var(--black)', padding: '1rem' }}
              >
                <p style={{ fontWeight: 900, margin: 0 }}>{name}</p>
                <p className="section-text" style={{ margin: 0 }}>
                  {job}
                </p>
              </div>
            ))}
          </div>
          <p className="section-text">
            We also had an admin dashboard and a web dashboard for restaurant partners.
          </p>

          <div className="image-grid zipem-image-grid">
            <ImageFigure
              className="image-grid-item"
              src={restaurantDashboard}
              alt="Zipem restaurant app dashboard"
              caption="Restaurant home: new orders, today's sales and order status counts"
              onOpen={openLightbox}
            />
            <ImageFigure
              className="image-grid-item"
              src={restaurantOrderDetails}
              alt="Zipem restaurant order details"
              caption="Restaurant: customer, items and bill on one screen, so reviewing leads straight to acting"
              onOpen={openLightbox}
            />
            <ImageFigure
              className="image-grid-item"
              src={deliveryOrderDetails}
              alt="Zipem delivery partner order details"
              caption="Delivery: customer, address, items and bill together"
              onOpen={openLightbox}
            />
            <ImageFigure
              className="image-grid-item"
              src={deliveryOrderMap}
              alt="Zipem delivery partner map of the destination"
              caption="Delivery: map of the destination"
              onOpen={openLightbox}
            />
          </div>
        </section>

        {/* MORE SCREENS */}
        <section className="section">
          <h2 className="section-title">More Screens</h2>
          <div className="image-grid zipem-image-grid">
            <ImageFigure
              className="image-grid-item"
              src={offersScreen}
              alt="Zipem offers"
              caption="Offers"
              onOpen={openLightbox}
            />
            <ImageFigure
              className="image-grid-item"
              src={vouchersScreen}
              alt="Zipem vouchers"
              caption="Vouchers"
              onOpen={openLightbox}
            />
            <ImageFigure
              className="image-grid-item"
              src={cartScreen}
              alt="Zipem cart and checkout"
              caption="Cart and checkout"
              onOpen={openLightbox}
            />
            <ImageFigure
              className="image-grid-item"
              src={restaurantFoodEditor}
              alt="Zipem restaurant food item editor"
              caption="Restaurant: menu item editor"
              onOpen={openLightbox}
            />
            <ImageFigure
              className="image-grid-item"
              src={restaurantSalesReport}
              alt="Zipem restaurant sales report"
              caption="Restaurant: sales and billing breakdown"
              onOpen={openLightbox}
            />
            <ImageFigure
              className="image-grid-item"
              src={deliveryHome}
              alt="Zipem delivery partner home with availability toggle and running orders"
              caption="Delivery: availability and running orders"
              onOpen={openLightbox}
            />
          </div>
        </section>

        {/* CLOSURE */}
        <div className="innovation-block">
          <h3>Why We Closed</h3>
          <p>
            We built Zipem without external funding. When the March 2020 lockdown stopped restaurant operations and deliveries, we did not have the financial runway to keep the business going for long. After several difficult months, we shut the platform down in mid-2020.
          </p>
        </div>

        {/* LEARNINGS */}
        <div className="lessons">
          <h3>What I Took From Zipem</h3>
          <ul>
            <li>
              <strong>Not every item needs the same treatment:</strong> not every item
              deserves the same visual weight, and sales data shows which ones
              do.
            </li>
            <li>
              <strong>Make repeat orders easier, but keep the choice:</strong> reusing a customer's last
              choice saves effort only if they can still change it.
            </li>
            <li>
              <strong>Think about the whole order:</strong> a decision in the
              customer app lands on restaurant, packing and delivery screens
              too.
            </li>
            <li>
              <strong>Operations taught me about design:</strong> onboarding
              restaurants and answering support requests showed me constraints
              no screen review would.
            </li>
            <li>
              <strong>A good product also needs a sustainable business:</strong> Zipem reached
              110 restaurants, but a bootstrapped balance sheet could not
              absorb a lockdown.
            </li>
          </ul>
        </div>
      </div>

      <section className="next-project">
        <h2>Next Case Study</h2>
        <p
          style={{
            marginBottom: '3rem',
            fontSize: '1.2rem',
            color: 'var(--gray)',
          }}
        >
          See how I designed a healthcare form builder with conditional logic.
        </p>
        <Link to="/healthcare" className="next-btn">
          Healthcare Form Builder →
        </Link>
      </section>

      <Footer />

      {/* LIGHTBOX */}
      {lightboxImage && (
        <div
          className="zipem-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={lightboxImage.alt}
          onClick={closeLightbox}
        >
          <button
            type="button"
            className="zipem-lightbox-close"
            onClick={closeLightbox}
            aria-label="Close image"
          >
            ×
          </button>
          <img
            src={lightboxImage.image}
            alt={lightboxImage.alt}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}

      <style>{`
        .zipem-image-button {
          display: block;
          width: 100%;
          padding: 0;
          border: 0;
          background: transparent;
          cursor: zoom-in;
        }

        .zipem-image-button img {
          display: block;
          width: 100%;
          height: auto;
          max-height: 720px;
          object-fit: contain;
          border-radius: 8px;
        }

        .image-full figcaption,
        .image-grid-item figcaption {
          margin-top: 0.85rem;
          color: var(--gray);
          font-size: 0.95rem;
          line-height: 1.5;
          text-align: center;
        }

        .zipem-image-grid .image-grid-item {
          min-width: 0;
          margin: 0;
        }

        .zipem-image-grid .image-grid-item img {
          max-height: 520px;
        }

        .zipem-lightbox {
          position: fixed;
          inset: 0;
          z-index: 3000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          background: rgba(0, 0, 0, 0.92);
        }

        .zipem-lightbox img {
          display: block;
          max-width: 95vw;
          max-height: 90vh;
          width: auto;
          height: auto;
          object-fit: contain;
        }

        .zipem-lightbox-close {
          position: absolute;
          top: 1rem;
          right: 1.5rem;
          border: 0;
          background: transparent;
          color: white;
          font-size: 3rem;
          line-height: 1;
          cursor: pointer;
        }

        @media (max-width: 768px) {
          .zipem-lightbox {
            padding: 1rem;
          }
        }
      `}</style>
    </PageTransition>
  );
};

export default FoodDeliveryCase;